import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import queryString from 'query-string';
import {Location} from '@reach/router';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';
import {usePhaseState} from 'funnel-schedule/phase-context';
import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import Section, {SectionImage} from 'components/Elements/Section';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import ShareButtons from 'components/Social/ShareButtons';
import Footer from 'components/Elements/Footer';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import Video from 'components/Video';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendarCheck} from '@fortawesome/pro-light-svg-icons/faCalendarCheck';
import {faClock} from '@fortawesome/pro-light-svg-icons/faClock';
import {faShareSquare} from '@fortawesome/pro-light-svg-icons/faShareSquare';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import IconList, {IconListItem} from 'components/Elements/IconList';
import headerImage from 'static/backgrounds/1166044116-square.jpg';
import TimerSection from 'components/Landing/TimerSection';
import useBalanceText from 'utils/useBalanceText';
import {TestimonialBubble} from 'components/Testimonial';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  noShare = false,
  noFixedVideo = false,
  noExitIntent = false,
  slug = 'masterclass',
  navigate,
  variant = null,
  ...props
}) {
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();

  useBalanceText();
  return (
    <div className="page d-flex flex-column">
      <PageMeta />
      <div className="page-content">
        <Header className="background-purple header-wrap" logoWhite style="dark" />

        <Section
          id="landing-header-cta"
          className={classNames(
            `section-angle-first section-landing-header-cta section-landing-header-cta-${defaultSchedule.phase} section-landing-default`
          )}
          color="black"
          innerClass="text-white text-center"
          angle="none"
        >
          <Container>
            <Row className={classNames(`d-flex justify-content-center justify-content-lg-end align-items-center`)}>
              <Col xs="12" lg="7">
                {variant === 'ad' ? (
                  <>
                    <p className="balance-text mt-4">FREE Masterclass Now Playing For A Limited Time!</p>
                    <h2 className="my-4">Stay Heart Healthy With These 10 Heart Health Keys</h2>
                    <h5 className="mt-0">
                      Join the Free <i>Healthy Heart Masterclass</i> for 4 heart health myths and 10 recent scientific
                      breakthroughs (that many doctors don’t even know)
                    </h5>
                    <p className="balance-text">
                      Get the truth about what works for a healthy heart. Watch the <i>Healthy Heart Masterclass</i>{' '}
                      today.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="balance-text mt-4">FREE Masterclass Now Playing For A Limited Time!</p>
                    <h2 className="my-4">
                      At last: A science-based way to avoid heart disease — or even <i>reverse</i> it
                    </h2>
                    <h5 className="mt-0">
                      Discover the 4 biggest heart disease myths and 10 recent scientific breakthroughs in the free{' '}
                      <i>Healthy Heart Masterclass</i>
                    </h5>
                    <p className="balance-text">
                      Stop heart disease from stopping you. Watch the <i>Healthy Heart Masterclass</i> today.
                    </p>
                  </>
                )}
                <div className="d-lg-none">
                  <Row className="d-flex justify-content-center my-4">
                    <Col xs="8" sm="7">
                      <img className="img-fluid border-solid border-white border-5 rounded-circle" src={headerImage} />
                    </Col>
                  </Row>
                </div>

                {variant === 'ad' ? null : (
                  <>
                    {defaultSchedule.phase === 'during' ? (
                      <TimerSection
                        launchEnd={launchEnd}
                        timerHeading={defaultSchedule.launchTitle}
                        className="text-center"
                      />
                    ) : null}
                    {defaultSchedule.phase === 'after' ? (
                      <h4 className="mt-4 mb-2">
                        The <span className="text-lowercase">{defaultSchedule.launchTitle}</span> period is over... But
                        we can still get you in!
                      </h4>
                    ) : null}
                  </>
                )}

                <div className="mt-4">
                  <OptinModalTrigger>
                    Reserve Your <br className="d-none d-lg-block d-xl-none" />
                    <i>Free</i> Spot Now
                  </OptinModalTrigger>
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-masterclass-info" className="section-angle-bottom-offset" color="green" angle="top">
          <Container className="py-5">
            <Row className="m-0">
              <Col xs="12" className="p-0">
                <Container className="background-white box-shadow-black-25 rounded p-4 py-lg-5" fluid>
                  <Row className="my-3">
                    <Col xs="12">
                      <h4 className="section-heading text-center">
                        {variant === 'ad' ? (
                          <>Get The Keys To Heart Health In This 90-Minute Masterclass</>
                        ) : (
                          <>In This 90-Minute Masterclass You Will:</>
                        )}
                      </h4>
                      <Row className="d-flex justify-content-center">
                        <Col xs="12" lg="11">
                          <IconList className="mb-0">
                            <IconListItem>
                              {variant === 'ad' ? (
                                <>
                                  <b>The four biggest (and outdated) heart health myths</b> that can zap energy,
                                  increase rates of obesity.
                                </>
                              ) : (
                                <>
                                  <b>Discover the four pervasive myths about heart health</b> that many doctors still
                                  believe (hint: they’re why heart disease is still the #1 killer in the world, despite
                                  amazing medical advances)
                                </>
                              )}
                            </IconListItem>
                            <IconListItem>
                              {variant === 'ad' ? (
                                <>
                                  <b>What works (and what most people are missing)</b> when it comes to keeping a
                                  healthy heart, and what the latest science says about the best foods to eat for
                                  maximum heart health.
                                </>
                              ) : (
                                <>
                                  <b>Cut through the hype and controversies</b> and learn which foods heal — and which
                                  harm — your heart
                                </>
                              )}
                            </IconListItem>
                            <IconListItem>
                              {variant === 'ad' ? (
                                <>
                                  <b>How it’s possible to “switch off” certain genes</b> — using natural, delicious, and
                                  wholesome foods… and why pills and surgeries are not the “solution” we thought.
                                </>
                              ) : (
                                <>
                                  <b>
                                    Find out how a simple 2-minute daily dental hygiene routine can significantly cut
                                    your risk
                                  </b>{' '}
                                  of heart disease
                                </>
                              )}
                            </IconListItem>
                            <IconListItem>
                              {variant === 'ad' ? (
                                <>
                                  <b>Hear the little-known truth about how the heart and body weight are connected.</b>{' '}
                                  This information can help us feel lighter and better — and it could even save lives.
                                </>
                              ) : (
                                <>
                                  <b>
                                    Understand how a condition that you may not even realize you have can really damage
                                    your heart
                                  </b>{' '}
                                  in the long run — and how to find out whether you’re at risk
                                </>
                              )}
                            </IconListItem>
                            <IconListItem>
                              {variant === 'ad' ? (
                                <>
                                  <b>The top foods to NEVER eat…</b> and why some ingredients found in common “health”
                                  foods are worse than we thought.
                                </>
                              ) : (
                                <>
                                  <b>The single best exercise for your heart</b> — that doesn’t require a gym
                                  membership, weights, or even a lot of room — that you’ll enjoy so much, you won’t have
                                  to “force yourself” to do it
                                </>
                              )}
                            </IconListItem>
                            <IconListItem>
                              {variant === 'ad' ? (
                                <>
                                  <b>
                                    Why everyday stress, danger, and loneliness play a bigger role in heart health than
                                    we once realized.
                                  </b>{' '}
                                  (The heart is{' '}
                                  <b>
                                    <i>much</i>
                                  </b>{' '}
                                  different from other organs for this one KEY reason.)
                                </>
                              ) : (
                                <>
                                  <b>Learn how your emotions affect your blood pressure</b> and even your cholesterol
                                </>
                              )}
                            </IconListItem>
                            <IconListItem>
                              <b>Get introduced to a simple breathing technique</b> that can instantly reduce stress and
                              help you feel more peaceful
                            </IconListItem>
                            <IconListItem>
                              <b>Avoid the number one mistake that stops people from making progress</b> when they start
                              a new health program
                            </IconListItem>
                          </IconList>
                          <h2 className="text-center mt-4 mb-3">
                            Heart disease is the leading cause of death on the planet.
                          </h2>
                          <h4 className="text-center m-0">It’s time to stop living in fear and get the truth.</h4>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-video" color="light-gray" innerClass="text-center" angle="both">
          <Container>
            <Row className="justify-content-center">
              <Col xs="12" lg="10" className="video-col px-0 px-sm-3">
                <div className="px-3 px-sm-0 mb-3">
                  <p className="mb-0">
                    <FontAwesomeIcon icon={faArrowDown} /> Watch the Trailer <FontAwesomeIcon icon={faArrowDown} />
                  </p>
                </div>
                <div>
                  <Video
                    url="https://www.youtube.com/watch?v=swNqrf_gxbk"
                    label="HHC - Masterclass Trailer"
                    thumbnail="https://cdn.foodrevolution.org/hhc/videothumb/hhc-dynamicthumbnail-202308-v2.gif"
                    noButton
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        <Section id="landing-steps" color="white">
          <Container>
            <Row className="d-flex align-items-stretch justify-content-center text-center">
              <Col xs="12" lg="4" className="d-flex flex-column mb-5 mb-lg-0">
                <div className="icon">
                  <FontAwesomeIcon className="text-green text-large" icon={faCalendarCheck} />
                </div>
                <h5>Add to Your Calendar</h5>
                <p className="balance-text my-auto">
                  Clear your schedule, and treat yourself (and your heart!) to some well-deserved me-time.
                </p>
              </Col>
              <Col xs="12" lg="4" className="d-flex flex-column mb-5 mb-lg-0">
                <div className="icon">
                  <FontAwesomeIcon className="text-green text-large" icon={faClock} />
                </div>
                <h5>90 Minutes</h5>
                <p className="balance-text my-auto">
                  Schedule approximately 90 minutes of quiet time for this Masterclass.
                </p>
              </Col>
              <Col xs="12" lg="4" className="d-flex flex-column">
                <div className="icon">
                  <FontAwesomeIcon className="text-green text-large" icon={faShareSquare} />
                </div>
                <h5>Share This Masterclass</h5>
                <p className="balance-text my-auto">
                  Help your loved ones stay healthy. Share this Masterclass with your friends and family.
                </p>
              </Col>
            </Row>
          </Container>
        </Section>
        <SectionImage
          id="landing-host-mimi"
          color="black"
          innerClass="text-white"
          imgPosition="left"
          imgUrl={require('static/speaker/mimi-guarneri-headshot.jpg')}
        >
          <h3 className="section-heading">Who is Dr. Mimi Guarneri?</h3>
          <p>
            You may recognize Holistic Cardiologist Dr. Mimi Guarneri from many globally-syndicated television shows
            including <i>NBC Today,</i> <i>PBS,</i> and <i>The Dr. Oz Show.</i>
          </p>
          <p className="balance-text">
            Dr. Guarneri’s passion for heart health was born after tragedy struck when her mother died of heart issues
            at age 39, and her father at age 50. Today, she’s an award-winning cardiac specialist who’s guided millions
            of people to uncover holistic solutions that are backed by the leading edge of medical research. What she
            shares with you could make all the difference.
          </p>
        </SectionImage>
        <SectionImage
          id="landing-host-ocean"
          color="black"
          innerClass="text-white"
          imgPosition="right"
          imgUrl={require('static/speaker/ocean-robbins-headshot.jpg')}
        >
          <h3 className="section-heading">Hosted by Ocean Robbins</h3>
          <p className="balance-text">
            Ocean Robbins is the co-founder and CEO of the 700,000+ member Food Revolution Network, and his work has
            reached millions of people from 180+ nations. He was born in a log cabin built by his parents and was raised
            on food they grew on the land together.
          </p>
          <p className="balance-text">
            Ocean’s mission has taken him all over the world, where he’s seen first-hand the powerful impact of the food
            we eat. He has served as an adjunct professor for Chapman University and is author of the national
            bestseller, <i>31-Day Food Revolution.</i>
          </p>
          <p className="balance-text">Ocean and Dr. Mimi can’t wait to share this Masterclass with you.</p>
        </SectionImage>

        <SectionImage
          id="landing-cta"
          className="landing-final-cta"
          color="purple"
          innerClass="text-white text-center"
          imgPosition="left"
          imgUrl={require('static/backgrounds/857552158.jpg')}
        >
          <h2 className="section-heading mb-2">{variant === 'ad' ? 'The' : 'Your'} heart is worth protecting.</h2>
          <h3 className="mt-0">Take action today.</h3>

          {variant === 'ad' ? null : (
            <>
              {defaultSchedule.phase === 'during' ? (
                <TimerSection launchEnd={launchEnd} timerHeading={defaultSchedule.launchTitle} />
              ) : null}
              {defaultSchedule.phase === 'after' ? (
                <h4 className="mt-4 mb-2">
                  The <span className="text-lowercase">{defaultSchedule.launchTitle}</span> period is over... But we can
                  still get you in!
                </h4>
              ) : null}
            </>
          )}

          <div className="mt-4">
            <OptinModalTrigger>
              Reserve Your <i>Free</i> Spot Now
            </OptinModalTrigger>
          </div>
          <p className="balance-text mt-4">This Masterclass is absolutely FREE and plays in your local time.</p>
        </SectionImage>
      </div>

      <div className="footer-content mt-auto">
        <Footer />
        <ShareButtons floating />
      </div>

      <OptinModal variant={variant} />
      {noExitIntent ? null : <ModalExitLanding />}
    </div>
  );
}
